/**
 * Чистая логика экрана «Оборачиваемость»: форматирование, сортировка, поиск.
 *
 * Метрик здесь не считают — только показывают и упорядочивают пришедшие с
 * бэкенда (`warehouse_turnover/service.py`).
 */
import { expenseDocTitle } from "@/api/warehouse_turnover";

const rub0 = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const days0 = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const days1 = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });

const qty0 = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

/** Средний остаток в штуках — целым: это среднее по срезам, и «14 381,571 шт»
 *  только мешает читать. Округление — вывода, не расчёта. */
export function fmtQtyInt(v: number): string {
  return qty0.format(v);
}

/** Рубли без копеек: в таблице миллионов копейки — шум. */
export function fmtRub(v: number): string {
  return rub0.format(v);
}

/** Подсказка к «—» в днях. */
export const NO_EXPENSE_HINT = "нет расхода за период";

/**
 * Дни на экран: `null` (расхода нет) — «—», НЕ ноль: ноль дней читался бы как
 * «уходит мгновенно», то есть ровно наоборот. До 10 дней — с десятыми.
 */
export function fmtDays(v: number | null): string {
  if (v === null) return "—";
  return (Math.abs(v) < 10 ? days1 : days0).format(v);
}

/** 1С отдаёт период без таймзоны — берём день и месяц из строки как есть,
 *  не прогоняя через `Date` (иначе браузер сдвинул бы его своей зоной). */
export function fmtDayMonth(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}` : iso;
}

/** Разбивка расхода по документам: ненулевые, в порядке ответа, с подписью. */
export function docBreakdown(
  byDoc: Record<string, number>,
  fmt: (v: number) => string,
): string {
  return Object.entries(byDoc)
    .filter(([, v]) => v !== 0)
    .map(([k, v]) => `${expenseDocTitle(k)} ${fmt(v)}`)
    .join(" · ");
}

/** Доли документов расхода для полоски разбивки: только положительные, в
 *  порядке ответа; доля — от их суммы (это пропорция отрезков, не метрика). */
export function docShares(
  byDoc: Record<string, number>,
): { key: string; value: number; share: number }[] {
  const parts = Object.entries(byDoc).filter(([, v]) => v > 0);
  const total = parts.reduce((s, [, v]) => s + v, 0);
  if (total <= 0) return [];
  return parts.map(([key, value]) => ({ key, value, share: value / total }));
}

export type SortDir = "asc" | "desc";
export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/**
 * Клик по заголовку: новая колонка — числа по убыванию, текст по алфавиту;
 * та же — направление меняется; третий клик — снова «как пришло» (`null`).
 */
export function nextSort<K extends string>(
  cur: SortState<K> | null,
  key: K,
  isText: boolean,
): SortState<K> | null {
  const first: SortDir = isText ? "asc" : "desc";
  if (!cur || cur.key !== key) return { key, dir: first };
  if (cur.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
  return null;
}

/**
 * Копия, упорядоченная по колонке; без сортировки — порядок ответа.
 * `null` (нет расхода → дней нет) всегда в конце, в любом направлении:
 * это не «самое большое» и не «самое маленькое» число, а его отсутствие.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K> | null,
  get: (row: T, key: K) => number | string | null,
): T[] {
  if (!sort) return [...rows];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = get(a, sort.key);
    const vb = get(b, sort.key);
    if (va === null || vb === null) {
      if (va === vb) return 0;
      return va === null ? 1 : -1;
    }
    if (typeof va === "string" || typeof vb === "string") {
      return sign * String(va).localeCompare(String(vb), "ru");
    }
    return sign * (va - vb);
  });
}

/** Поиск по артикулу/названию: без регистра, пустой запрос — всё. */
export function filterItems<T extends { article: string; name: string }>(
  items: readonly T[],
  query: string,
): T[] {
  const q = query.trim().toLocaleLowerCase("ru");
  if (!q) return [...items];
  return items.filter(
    (i) =>
      i.article.toLocaleLowerCase("ru").includes(q) ||
      i.name.toLocaleLowerCase("ru").includes(q),
  );
}

/** Сколько позиций в категории нужно, чтобы появился поиск. */
export const SEARCH_MIN_ITEMS = 30;
/** Начальный вывод позиций: 400+ строк разом тормозят раскрытие на ТСД. */
export const ITEMS_INITIAL_LIMIT = 100;
